import { CrmCallbacks } from './CrmCallbacks';
import { PersonalFollowUps } from './PersonalFollowUps';
/**
 * Follow-ups Admin dashboard — CVG3-FU / L106 (2026-07-11).
 *
 * Team follow-ups surface for admin/owner + team-head roles. Backend list is
 * visibility-scoped server-side (getVisibleUserIds): team heads see their
 * team, admin/owner see all — the page just renders what it's given.
 *
 * Layout mirrors the L51/attendance-admin pattern (AttendanceAdminPage.tsx /
 * TeamTrackingPage.tsx): Tailwind, rounded-xl cards, gray-200 borders,
 * blue-600 accents, lucide-react icons, api via followUpsAdminApi.
 *
 * Row actions: reassign (role-ruled server-side, 403 surfaces as an error
 * toast line), complete, cancel. "Set by" column per L105 3c.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  BellRing, RefreshCw, Loader2, CalendarClock, AlertTriangle, CheckCircle2,
  UserRound, X, Check,
} from 'lucide-react';
import { FollowUpDoneDialog, type FollowUpDoneTarget } from './FollowUpDoneDialog';
import {
  cancelFollowUp,
  contactLabel,
  fetchDelegableUsers,
  fetchTeamFollowUps,
  fetchTeamFollowUpsSummary,
  notesLabel,
  phoneLabel,
  reassignFollowUp,
  setByLabel,
  type TeamFollowUpsSummary,
  type DelegableUser,
  type FollowUpRow,
} from './followUpsAdminApi';
import { leadStageLabel } from '../crm/leadStages';

const STAGES = ['cold', 'followup', 'quote', 'neg', 'hot', 'won', 'lost'] as const;
const STATUSES = ['PENDING', 'COMPLETED', 'MISSED', 'CANCELLED'] as const;

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit',
  });
}

function statusBadge(status: string) {
  const map: Record<string, string> = {
    PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
    COMPLETED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    MISSED: 'bg-red-50 text-red-700 border-red-200',
    CANCELLED: 'bg-gray-100 text-gray-500 border-gray-200',
  };
  return map[status] ?? 'bg-gray-100 text-gray-600 border-gray-200';
}

export function FollowUpsAdminPage({ personal = false, crm = false }: { personal?: boolean; crm?: boolean }) {
  if (crm) return <CrmCallbacks />;
  return personal ? <PersonalFollowUps /> : <TeamFollowUpsPage />;
}

function TeamFollowUpsPage() {
  const [rows, setRows] = useState<FollowUpRow[]>([]);
  const [total, setTotal] = useState(0);
  const [roster, setRoster] = useState<DelegableUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyRowId, setBusyRowId] = useState<string | null>(null);
  const [reassignRowId, setReassignRowId] = useState<string | null>(null);
  // 2026-10-07 (Manak) — ✓ opens the Done box (same as phone / My Follow-ups).
  const [doneTarget, setDoneTarget] = useState<FollowUpDoneTarget | null>(null);
  // Tiles = server summary (shared follow-ups feed + call rows it does not
  // cover; Completed (7d) = Done / auto-done / completed call rows). 2026-10-07.
  const [summary, setSummary] = useState<TeamFollowUpsSummary | null>(null);

  // Filters (L106 3d)
  const [agentId, setAgentId] = useState('');
  const [status, setStatus] = useState('');
  const [overdue, setOverdue] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [stage, setStage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchTeamFollowUps({
        agentId: agentId || undefined,
        status: status || undefined,
        overdue,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        stage: stage || undefined,
        limit: 100,
      });
      setRows(res.rows);
      setTotal(res.total);
      fetchTeamFollowUpsSummary(agentId || undefined).then(setSummary).catch(() => setSummary(null));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load follow-ups');
    } finally {
      setLoading(false);
    }
  }, [agentId, status, overdue, startDate, endDate, stage]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    fetchDelegableUsers().then(setRoster).catch(() => setRoster([]));
  }, []);

  const runRowAction = async (id: string, fn: () => Promise<void>) => {
    setBusyRowId(id);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error ?? 'Action failed');
    } finally {
      setBusyRowId(null);
      setReassignRowId(null);
    }
  };

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-blue-50 p-2">
            <BellRing className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Follow-ups</h1>
            <p className="text-sm text-gray-500">
              Team follow-ups · reassign, complete or cancel · {total} total
            </p>
          </div>
        </div>
        <button
          onClick={() => void load()}
          className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* KPI strip (L106 3d) */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <CalendarClock className="h-4 w-4 text-blue-600" /> Due today
          </div>
          <div data-testid="followups-due-today-count" className="mt-1 text-2xl font-semibold text-gray-900">{summary ? summary.dueToday : '—'}</div>
          {summary && (
            <div className="mt-1 text-xs text-gray-500">
              {summary.lead.dueToday} lead follow-up dates · {summary.call.dueToday} call follow-ups
            </div>
          )}
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <AlertTriangle className="h-4 w-4 text-red-500" /> Overdue
          </div>
          <div data-testid="followups-overdue-count" className="mt-1 text-2xl font-semibold text-gray-900">{summary ? summary.overdue : '—'}</div>
          {summary && (
            <div className="mt-1 text-xs text-gray-500">
              {summary.lead.overdue} lead follow-up dates · {summary.call.overdue} call follow-ups
            </div>
          )}
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Completed (7d)
          </div>
          <div data-testid="followups-completed-count" className="mt-1 text-2xl font-semibold text-gray-900">{summary ? summary.completed7d : '—'}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-4">
        <label className="text-sm text-gray-600">
          <span className="mb-1 block text-xs font-medium text-gray-500">Rep</span>
          <select
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
          >
            <option value="">All</option>
            {roster.map((u) => (
              <option key={u.id} value={u.id}>{u.name}{u.isSelf ? ' (me)' : ''}</option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-600">
          <span className="mb-1 block text-xs font-medium text-gray-500">Status</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
          >
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="text-sm text-gray-600">
          <span className="mb-1 block text-xs font-medium text-gray-500">Stage</span>
          <select
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
          >
            <option value="">All</option>
            {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="text-sm text-gray-600">
          <span className="mb-1 block text-xs font-medium text-gray-500">From</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="text-sm text-gray-600">
          <span className="mb-1 block text-xs font-medium text-gray-500">To</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="flex items-center gap-2 pb-1.5 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={overdue}
            onChange={(e) => setOverdue(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-blue-600"
          />
          Overdue only
        </label>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading follow-ups…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-500">
            No follow-ups match the current filters.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Agent</th>
                <th className="px-4 py-3">Set by</th>
                <th className="px-4 py-3">Scheduled</th>
                <th className="px-4 py-3">Stage</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Notes</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => {
                const busy = busyRowId === r.id;
                const setBy = setByLabel(r);
                return (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {contactLabel(r)}
                      <div className="text-xs font-normal text-gray-500">
                        {phoneLabel(r)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      <span className="inline-flex items-center gap-1.5">
                        <UserRound className="h-3.5 w-3.5 text-gray-400" />
                        {r.agent?.name ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {setBy ? (
                        <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">
                          Set by {setBy}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{fmtDateTime(r.scheduledAt)}</td>
                    <td className="px-4 py-3 text-gray-700">{leadStageLabel(r.leadStage)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${statusBadge(r.status)}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="max-w-[220px] truncate px-4 py-3 text-gray-500" title={notesLabel(r) === '—' ? '' : notesLabel(r)}>
                      {notesLabel(r)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {busy ? (
                        <Loader2 className="ml-auto h-4 w-4 animate-spin text-gray-400" />
                      ) : reassignRowId === r.id ? (
                        <span className="inline-flex items-center gap-1">
                          <select
                            defaultValue={r.agentId}
                            className="rounded-lg border border-gray-200 px-2 py-1 text-xs"
                            onChange={(e) => {
                              const to = e.target.value;
                              if (to && to !== r.agentId) {
                                void runRowAction(r.id, () => reassignFollowUp(r.id, to));
                              }
                            }}
                          >
                            {roster.map((u) => (
                              <option key={u.id} value={u.id}>{u.name}</option>
                            ))}
                          </select>
                          <button
                            title="Close"
                            onClick={() => setReassignRowId(null)}
                            className="rounded p-1 text-gray-400 hover:bg-gray-100"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <button
                            title="Reassign"
                            disabled={r.status !== 'PENDING'}
                            onClick={() => setReassignRowId(r.id)}
                            className="rounded-lg border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                          >
                            Reassign
                          </button>
                          <button
                            title="Mark completed"
                            disabled={r.status !== 'PENDING'}
                            onClick={() => setDoneTarget({ name: contactLabel(r), leadId: r.leadId ?? null, followUpRowId: r.id })}
                            className="rounded-lg border border-emerald-200 p-1.5 text-emerald-600 hover:bg-emerald-50 disabled:opacity-40"
                          >
                            <Check className="h-3.5 w-3.5" />
                          </button>
                          <button
                            title="Cancel follow-up"
                            disabled={r.status !== 'PENDING'}
                            onClick={() => void runRowAction(r.id, () => cancelFollowUp(r.id))}
                            className="rounded-lg border border-red-200 p-1.5 text-red-500 hover:bg-red-50 disabled:opacity-40"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {doneTarget && (
        <FollowUpDoneDialog
          target={doneTarget}
          onClose={() => setDoneTarget(null)}
          onSaved={() => { setDoneTarget(null); void load(); }}
        />
      )}
    </div>
  );
}

export default FollowUpsAdminPage;
